import * as R from 'ramda';

import { DataType } from '@atom/types/dataType';
import { FieldFilter } from '@atom/types/work';
import {
  convertDateToLocalDayEnd,
  convertDateToLocalDayStart,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

// The data types WorkOrderFieldFilterInput / TaskFieldFilterInput can render
const FIELD_FILTER_DATA_TYPES: DataType[] = [
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.ENUM_SINGLE,
  DataType.ENUM_MULTIPLE,
  DataType.NUMBER,
];

export const isFilterableDataType = (dataType: DataType): boolean =>
  FIELD_FILTER_DATA_TYPES.includes(dataType);

// A field filter carrying no values at all must be dropped from the connection
// input - the API treats a bare { id } as an unsatisfiable filter.
export const isEmptyFieldFilter = (fieldFilter: FieldFilter): boolean =>
  isNilOrEmpty(fieldFilter?.value) &&
  isNilOrEmpty(fieldFilter?.values) &&
  isNilOrEmpty(fieldFilter?.valueStart) &&
  isNilOrEmpty(fieldFilter?.valueEnd);

export const createFieldFilterHandlers = (
  fieldId: string,
  fieldFilter: FieldFilter | null,
  onChange: (updatedFilter: FieldFilter) => void,
) => {
  const updateFieldProperty = (fieldProperty: keyof FieldFilter, value: any) =>
    onChange({ ...fieldFilter, id: fieldId, [fieldProperty]: value });

  const getDateValue = (fieldProperty: keyof FieldFilter): Date | null =>
    fieldFilter && !isNilOrEmpty(fieldFilter[fieldProperty])
      ? new Date(Number(fieldFilter[fieldProperty]))
      : null;

  const handleDateChange =
    (fieldProperty: keyof FieldFilter) => (_, dateValue: Date) => {
      const dateMillis = dateValue
        ? fieldProperty === 'valueStart'
          ? convertDateToLocalDayStart(dateValue)
          : convertDateToLocalDayEnd(dateValue)
        : null;

      updateFieldProperty(fieldProperty, dateMillis);
    };

  const handleDateTimeChange =
    (fieldProperty: keyof FieldFilter) => (_, dateValue: Date) =>
      updateFieldProperty(
        fieldProperty,
        dateValue ? dateValue.getTime() : null,
      );

  const handleSelectChange =
    (fieldProperty: keyof FieldFilter) => (_, value: any) =>
      updateFieldProperty(fieldProperty, R.isEmpty(value) ? null : value);

  const handleNumericChange =
    (fieldProperty: keyof FieldFilter) => (_, value: any) =>
      updateFieldProperty(fieldProperty, value === '' ? null : Number(value));

  return {
    getDateValue,
    handleDateChange,
    handleDateTimeChange,
    handleSelectChange,
    handleNumericChange,
  };
};
